import type { MasteryMap } from './mastery.ts';
import { weakestScore } from './mastery.ts';
import type { Rng } from './rng.ts';
import { pickN } from './rng.ts';

export interface ReviewCandidate {
  character: string;
  isWriteTarget: boolean;
  /** The tone of this character in the word it's taught with, when known. */
  tone?: number;
}

/**
 * Pick characters for an automatic review: everything due, then recent mistakes / weakest,
 * plus ~20% sampled from mastered items so they don't decay unseen. `opts.weakTones` nudges
 * characters carrying a tone the child struggles with earlier among the non-due items (it never
 * promotes them ahead of anything actually due).
 */
export function selectReviewCharacters(
  candidates: readonly ReviewCandidate[],
  mastery: MasteryMap,
  now: Date,
  n: number,
  rng: Rng,
  opts?: { weakTones?: ReadonlySet<number> },
): string[] {
  const nowMs = now.getTime();
  const due: string[] = [];
  const rest: Array<{ c: string; weak: number }> = [];
  for (const cand of candidates) {
    let isDue = false;
    for (const m of mastery.values()) {
      if (m.character !== cand.character || !m.dueAt) continue;
      if (Date.parse(m.dueAt) <= nowMs) isDue = true;
    }
    if (isDue) due.push(cand.character);
    else {
      const weak = weakestScore(mastery, cand.character, cand.isWriteTarget);
      const toneBias =
        cand.tone !== undefined && opts?.weakTones?.has(cand.tone) ? weak - 15 : weak;
      rest.push({ c: cand.character, weak: toneBias });
    }
  }
  const out = pickN(due, n, rng);
  if (out.length >= n) return out;
  rest.sort((a, b) => a.weak - b.weak);
  const strong = rest.filter((r) => r.weak >= 80).map((r) => r.c);
  const weak = rest.filter((r) => r.weak < 80).map((r) => r.c);
  const strongQuota = Math.min(strong.length, Math.round((n - out.length) * 0.2));
  const fromWeak = weak.slice(0, n - out.length - strongQuota);
  const fromStrong = pickN(strong, n - out.length - fromWeak.length, rng);
  return [...out, ...fromWeak, ...fromStrong];
}
